import React, { useState, useEffect } from "react";
import { Lock, Save, CheckCircle2, RefreshCw, AlertCircle } from "lucide-react";
import { fetchCategorySettings, updateCategorySettings } from "../system/settingsApi";

export const LoginSecurityPage: React.FC = () => {
  const [security, setSecurity] = useState({
    max_failed_attempts: 5,
    lockout_duration_minutes: 15,
    captcha_required: false,
    ip_whitelist_enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchCategorySettings("login_security");
      if (res.data) {
        setSecurity((prev) => ({ ...prev, ...res.data }));
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load login security parameters.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await updateCategorySettings("login_security", security);
      setSaved(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save login security parameters.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-xs">
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-indigo-400" />
        <span>Loading brute force protection parameters...</span>
      </div>
    );
  }

  return (
    <div className="p-5 bg-slate-900/90 border border-slate-800 rounded-xl shadow-md text-xs font-sans space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-950/60 border border-red-800 text-red-300 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-indigo-400" />
          <h2 className="text-base font-bold text-slate-100">Brute Force Protection & Lockout Parameters</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-lg shadow-md transition-colors text-xs"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin text-white" />
              <span>Saving...</span>
            </>
          ) : saved ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-white" />
              <span>Saved!</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save Parameters</span>
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">
            Failed Password Attempt Threshold
          </label>
          <input
            type="number"
            value={security.max_failed_attempts}
            onChange={(e) => {
              setSaved(false);
              setSecurity({ ...security, max_failed_attempts: Number(e.target.value) });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono font-bold"
          />
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">
            Account Lockout Duration (Minutes)
          </label>
          <input
            type="number"
            value={security.lockout_duration_minutes}
            onChange={(e) => {
              setSaved(false);
              setSecurity({ ...security, lockout_duration_minutes: Number(e.target.value) });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono font-bold"
          />
        </div>
      </div>

      <div className="space-y-3 font-mono">
        <div className="flex items-center justify-between p-3.5 bg-slate-950 border border-slate-800 rounded-xl">
          <div>
            <h4 className="font-bold text-slate-100 font-sans">Enforce CAPTCHA Verification on Repeated Failures</h4>
            <p className="text-slate-400 text-[11px] font-sans">Show Cloudflare Turnstile CAPTCHA challenge after 3 failed login attempts.</p>
          </div>
          <input
            type="checkbox"
            checked={security.captcha_required}
            onChange={(e) => {
              setSaved(false);
              setSecurity({ ...security, captcha_required: e.target.checked });
            }}
            className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-indigo-500"
          />
        </div>
      </div>
    </div>
  );
};
